/*
 * Copyright (c) 2025 Frayo and HomeChef360. All Rights Reserved.
 *
 * CONFIDENTIAL & PROPRIETARY
 * This software and its source code are the confidential and proprietary
 * information of Frayo and HomeChef360. Unauthorized copying, modification,
 * distribution, disclosure, or use of this code, in whole or in part, via any
 * medium, is strictly prohibited without prior written consent.
 *
 * Violations will be subject to legal action in the State of Florida and may
 * also be pursued under applicable U.S. federal law.
 */

import React, { FC, useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import clsx from "clsx";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ChefBasicProfile, useAuth } from "../../../auth";
import { UpdatePassword } from "../core/_requests";
import { passwordSchema } from "../core/Schema";

type Props = {
  initialValues: any;
};

interface ChefPortalData {
  verifyOtpEmail?: boolean;
  // Add other properties you need from localStorage
}

const ChangePassword: FC<Props> = ({ initialValues }) => {
  const [showAlert, setShowAlert] = useState(true);
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(initialValues);
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSocialLogin, setIsSocialLogin] = useState(true); // Default to true for safety
  const inputs = useRef<Array<HTMLInputElement | HTMLSelectElement | null>>([]);

  // Get chef data from localStorage
  const getChefPortalData = (): ChefPortalData | null => {
    try {
      const data = localStorage.getItem('frayoChefPortal');
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('Error parsing frayoChefPortal data:', error);
      return null;
    }
  };

  // Check login type on component mount
  useEffect(() => {
    const chefData = getChefPortalData();
    if (chefData) {
      // If verifyOtpEmail is true, it's a normal login
      const isNormalLogin = chefData.verifyOtpEmail === true;
      setIsSocialLogin(!isNormalLogin);
      
      if (!isNormalLogin) {
        toast.info("Social login users must change password through their provider", {
          autoClose: 5000
        });
      }
    }
  }, []);

  const handleTogglePassword = () => {
    if (isSocialLogin) return;
    setShowPassword(!showPassword);
  };

  const clickCancel = () => {
    navigate("/settings");
  };

  const formik = useFormik<ChefBasicProfile>({
    initialValues,
    validationSchema: isSocialLogin ? Yup.object().shape({}) : passwordSchema,
    onSubmit: async (values, { resetForm, setStatus, setSubmitting }) => {
      if (isSocialLogin) {
        toast.error("Password change not available for social login accounts");
        return;
      }

      setLoading(true);
      const updatedData = Object.assign(data, values);
    
      try {
        const res = await UpdatePassword(updatedData, currentUser?.uuid);
        if (res) {
          setLoading(false);
          toast.success(res?.Message || "Password updated successfully", { 
            autoClose: 5000 
          });
          navigate("/settings/password-success", { replace: true });
        } else {
          setLoading(false);
          setErrorMessage(res?.message || "Password update failed");

          if (updatedData.currentpassword !== updatedData.newpassword) {
            if (updatedData.newpassword === updatedData.confirmpassword) {
              const retryRes = await UpdatePassword(updatedData, currentUser?.uuid);
              if (retryRes) {
                setLoading(false);
                toast.success(retryRes?.message || "Password updated successfully", { 
                  autoClose: 5000 
                });
                navigate("/profile-success", { replace: true });
              } else {
                setLoading(false);
                toast.error(retryRes?.message || "Password update failed");
              }
              resetForm();
            } else {
              toast.error("New Password and Confirm Password must match");
              setLoading(false);
              setShowAlert(true);
            }
          } else {
            toast.error("Current Password and New Password cannot be the same");
            setLoading(false);
            setShowAlert(true);
          }
        }
      } catch (error) {
        setLoading(false);
        toast.error("An error occurred during password update");
      }
    },
  });

  if (isSocialLogin) {
    return (
      <section className="container-fluid">
        <div className="row">
          <section className="content-section">
            <div className="overlay"></div>
            <div className="drawer small">
              <div className="title row">
                <h3 className="col-10 mb-0">Change Password</h3>
                <div className="col-2 text-end mb-2">
                  <button
                    className="btn p-0 close d-inline-flex"
                    onClick={clickCancel}
                  >
                    <i className="material-symbols-outlined">close</i>
                  </button>
                </div>
              </div>
              <div className="p-4 text-center">
                <h5>Social Login Account</h5>
                <p className="text-muted">
                  Your account is linked to a social provider. Please change your password
                  directly through your social account settings.
                </p>
                <button 
                  className="btn btn-outline-primary rounded-5"
                  onClick={clickCancel}
                >
                  Back to Settings
                </button>
              </div>
            </div>
          </section>
        </div>
      </section>
    );
  }

  // ... rest of your existing return JSX ...
  return (
    <>
      <section className="container-fluid">
        <div className="row">
          <section className="content-section">
            <div className="overlay"></div>
            <div className="drawer small">
              <div className="title row">
                <h3 className="col-10 mb-0">Change Password</h3>
                <div className="col-2 text-end mb-2">
                  <button
                    className="btn p-0 close d-inline-flex"
                    onClick={clickCancel}
                  >
                    <i className="material-symbols-outlined">close</i>
                  </button>
                </div>
              </div>
              <form
                onSubmit={formik.handleSubmit}
                noValidate
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                  }
                }}
              >
                <div className="col-12 text-start mb-4 mt-3">
                  <label htmlFor="newpassword" className="small-font mb-2">
                    Current Password
                  </label>
                  <div className="input-group">
                    <input
                      placeholder="Give your current password"
                      type={showPassword ? "text" : "password"}
                      className={clsx("form-control", {
                        "is-invalid":
                          formik.touched.currentpassword &&
                          formik.errors.currentpassword,
                      })}
                      ref={(el) => {
                        inputs.current[3] = el;
                      }}
                      {...formik.getFieldProps("currentpassword")}
                    />
                    <div className="input-group-text">
                      <span
                        className="cursorPointer"
                        onClick={handleTogglePassword}
                      >
                        <i className="material-symbols-outlined">
                          {showPassword
                            ? "visibility"
                            : "visibility_off"}
                        </i>
                      </span>
                    </div>
                  </div>
                  {formik.touched.currentpassword &&
                    formik.errors.currentpassword && (
                      <div className="fv-plugins-message-container">
                        <span role="alert">
                          {formik.errors.currentpassword}
                        </span>
                      </div>
                    )}
                </div>
                <div className="col-12 text- start mb-4">
                  <label htmlFor="newpassword" className="small-font mb-2">
                    New Password
                  </label>
                  <div className="input-group">
                    <input
                      placeholder="Give your change password"
                      type={showPassword ? "text" : "password"}
                      className={clsx("form-control", {
                        "is-invalid":
                          formik.touched.newpassword &&
                          formik.errors.newpassword,
                      })}
                      ref={(el) => {
                        inputs.current[3] = el;
                      }}
                      {...formik.getFieldProps("newpassword")}

                    />
                    <div className="input-group-text">
                      <span
                        className="cursorPointer"
                        onClick={handleTogglePassword}
                      >
                        <i className="material-symbols-outlined">
                          {showPassword
                            ? "visibility"
                            : "visibility_off"}
                        </i>
                      </span>
                    </div>
                  </div>
                  {formik.touched.newpassword && formik.errors.newpassword && (
                    <div className="fv-plugins-message-container">
                      <span role="alert">{formik.errors.newpassword}</span>
                    </div>
                  )}
                </div>
                <div className="col-12 text-start mb-4">
                  <label htmlFor="newpassword" className="small-font mb-2">
                    Confirm Password
                  </label>
                  <div className="input-group">
                    <input
                      placeholder="Give your confirm password"
                      type={showPassword ? "text" : "password"}
                      className={clsx("form-control", {
                        "is-invalid":
                          formik.touched.confirmpassword &&
                          formik.errors.confirmpassword,
                      })}
                      ref={(el) => {
                        inputs.current[3] = el;
                      }}
                      {...formik.getFieldProps("confirmpassword")}
                    />
                    <div className="input-group-text">
                      <span
                        className="cursorPointer"
                        onClick={handleTogglePassword}
                      >
                        <i className="material-symbols-outlined">
                          {showPassword
                            ? "visibility"
                            : "visibility_off"}
                        </i>
                      </span>
                    </div>
                  </div>
                  {formik.touched.confirmpassword &&
                    formik.errors.confirmpassword && (
                      <div className="fv-plugins-message-container">
                        <span role="alert">
                          {formik.errors.confirmpassword}
                        </span>
                      </div>
                    )}
                </div>
                <div className="col-12 mb-3 mt-4 pt-3">
                  <p className="fw-med mb-3">
                    <small>Password must have</small>
                  </p>
                  <ul className="pwd-criteria-list">
                    <li>Lower & Upper case</li>
                    <li>At least 1 number or symbol</li>
                    <li>At least 6 characters</li>
                    <li>Cannot contain spaces and "|" symbol</li>
                  </ul>
                </div>
                <div className="col-12 mt-3">
                  <div className="row">
                    <div className="col-4"></div>
                    <div className="col-8 text-end">
                      <button
                        className="btn btn-cancel rounded-5"
                        onClick={clickCancel}
                      >
                        Cancel
                      </button>
                      <button
                        disabled={formik.isSubmitting || !formik.isValid}
                        className="btn btn-outline-primary rounded-5 me-2"
                      >
                        {!loading && (
                          <span className="indicator-label">Update Password</span>
                        )}
                        {loading && (
                          <span
                            className="indicator-progress"
                            style={{ display: "block" }}
                          >
                            Please wait...
                            <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                          </span>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </section>
        </div>
      </section>
    </>
  );
};

export { ChangePassword };